import { ScrollView, TouchableOpacity, View, Text } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { useCardBenefitsParameterStore } from '@/src/experiences/card-benefits/statsig/store/useCardBenefitsParameterStore'
import { paddingBottom } from '@/src/common/components/MesaFooter'
import { topTabBarHeight, useIsTabBarPositionedTop } from '@/src/common/components/TopTabBar'
import { useMediaQuery } from 'react-responsive'
import { chunk } from 'lodash'
import { maxWidth, useMainWrapperPadding } from '@/src/common/wrappers/MainWrapper'
import { useFeatureGate } from '@/src/services/Statsig/useFeatureGate'
import { FeatureGates } from '@/src/common/enums/FeatureGates'
import { Spinner } from 'tamagui'
import { DesignSystem } from '@/src/design-system'
import { useRef, useState } from 'react'
import { CardBenefitsComponents as Components } from '../components'
import { Icons } from '..'
import BottomSheet from '@gorhom/bottom-sheet'
import ChevronDown from '@/src/assets/icons/ChevronDown'
import ChevronUp from '@/src/assets/icons/ChevronUp'
import { Notifications } from '@/src/experiences/notifications'
import { mesaColorNames, typography } from '@/src/common/theme'
import { CardBenefitsE2E } from '@/src/experiences/card-benefits/e2e'
import { useAvailableBenefits } from '../hooks/useAvailableBenefits'

const FILTER_BENEFITS_BY = ['All', 'New', 'Enrolled', 'Not Enrolled'] as const

export function Benefits() {
  const learnMoreModalRef = useRef<BottomSheet>(null)
  const cardBenefitsStore = useCardBenefitsParameterStore()
  const learnMoreAboutCardBenefits = cardBenefitsStore.get('learn_more')

  const disclosures = cardBenefitsStore.get('disclosures', [])
  const isMesaEmployee = useFeatureGate(FeatureGates.MesaEmployees)

  const [selectedFilter, setSelectedFilter] = useState<(typeof FILTER_BENEFITS_BY)[number]>('All')
  const [isOpen, setIsOpen] = useState(false)

  const { availableBenefits, isLoading, userBenefitsEnrollments } =
    useAvailableBenefits(selectedFilter)

  const isTabBarPositionedTop = useIsTabBarPositionedTop()

  const minWidth1024 = useMediaQuery({ minWidth: 1024 })
  const minWidth720 = useMediaQuery({ minWidth: 720 })
  const mainWrapperPadding = useMainWrapperPadding()

  // we should handle if there is an error either of the fetches
  // with generic screen with retry
  if (isMesaEmployee.loadingStatus !== 'Ready' || isLoading) {
    return (
      <SafeAreaView
        edges={['top']}
        style={{
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: mesaColorNames['Mesa - Brand/Secondary #223843'],
          paddingTop: isTabBarPositionedTop ? topTabBarHeight + 32 : 16,
        }}
      >
        <Spinner color={mesaColorNames['Mesa - Brand/White #FFFFFF']} scale={1.5} />
      </SafeAreaView>
    )
  }

  if (!userBenefitsEnrollments) {
    return null
  }

  const columns = minWidth1024 ? 3 : minWidth720 ? 2 : 1
  const atLeast3Columns = columns >= 3
  const numberOfTilesInFullGrid = Math.ceil(availableBenefits.length / columns) * columns
  const numberOfTilesMissingFromFullGrid = numberOfTilesInFullGrid - availableBenefits.length
  const tiles = [
    ...availableBenefits,
    ...Array.from({ length: numberOfTilesMissingFromFullGrid }, () => undefined),
  ] as const
  const tilesChunked = chunk(tiles, columns)

  const rowGap = minWidth1024 ? 40 : 16
  const columnGap = minWidth1024 ? 60 : 24

  const showLearnMore = learnMoreAboutCardBenefits?.length

  return (
    <SafeAreaView
      edges={['top']}
      testID={CardBenefitsE2E.Ids.View.BenefitsIndex}
      style={{
        flex: 1,
        backgroundColor: mesaColorNames['Mesa - Brand/Secondary #223843'],
        paddingTop: isTabBarPositionedTop ? topTabBarHeight + 32 : 16,
        flexDirection: 'row',
      }}
    >
      <View
        style={{
          paddingHorizontal: mainWrapperPadding,
          maxWidth,
          marginHorizontal: 'auto',
          flex: 1,
        }}
      >
        <Notifications.Components.EnablePermissionsBanner text="Never miss any of Mesa's great offers and programs!" />

        {showLearnMore && (
          <TouchableOpacity
            onPress={() => {
              setIsOpen(!isOpen)
              isOpen
                ? learnMoreModalRef.current?.close()
                : learnMoreModalRef.current?.snapToIndex(0)
            }}
          >
            <DesignSystem.Card style={{ padding: 10, marginTop: 12, marginBottom: 20 }}>
              <View
                style={{
                  justifyContent: 'space-between',
                  flexDirection: 'row',
                  alignItems: 'center',
                  marginVertical: 16,
                }}
              >
                <Text style={[typography['heading/heading5'], { flex: 1 }]}>
                  Mesa Homeowners Card Benefits
                </Text>
                {isOpen ? <ChevronDown /> : <ChevronUp />}
              </View>
            </DesignSystem.Card>
          </TouchableOpacity>
        )}

        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{ alignItems: 'flex-start' }}
          style={{ flexShrink: 0, flexDirection: 'row', paddingBottom: 16 }}
        >
          {FILTER_BENEFITS_BY.map((filter) => (
            <DesignSystem.Chip
              key={filter}
              label={filter}
              selected={selectedFilter === filter}
              onPress={() => setSelectedFilter(filter)}
            />
          ))}
        </ScrollView>

        <ScrollView
          testID={CardBenefitsE2E.Ids.View.BenefitsTiles}
          contentContainerStyle={{ gap: 16, paddingBottom, rowGap }}
          showsVerticalScrollIndicator={false}
        >
          {!availableBenefits.length && (
            <View style={{ flex: 1, alignItems: 'center' }}>
              <Icons.CardBenefitsOutlined
                pathProps={{ stroke: mesaColorNames['Mesa Primary Scale/500 #00C7B6'] }}
                size={52}
              />
              <Text
                style={[
                  typography['heading/heading5'],
                  { paddingVertical: 18, color: mesaColorNames['Mesa - Brand/White #FFFFFF'] },
                ]}
              >
                {`You have no ${selectedFilter.toLowerCase()} benefits at this time`}
              </Text>
            </View>
          )}
          {tilesChunked.map((row) => (
            <View
              key={row.map((b) => b?.benefit.partnerName).join('-')}
              style={{ flexDirection: 'row', columnGap }}
            >
              {row.map((availableBenefit, index) => {
                if (!availableBenefit) return <View key={index} style={{ flex: 1 }} />

                return (
                  <Components.BenefitCard
                    key={availableBenefit.benefit.id}
                    availableBenefit={availableBenefit}
                    testID={CardBenefitsE2E.Ids.Press.Benefit(availableBenefit.benefit.partnerName)}
                    userBenefitsEnrollments={userBenefitsEnrollments}
                    style={{
                      flex: 1,
                      flexDirection: atLeast3Columns ? 'column' : 'row',
                      gap: 8,
                      alignItems: atLeast3Columns ? 'flex-start' : 'center',
                      ...(!atLeast3Columns && { height: 300 }),
                    }}
                    showChevron={!atLeast3Columns}
                  />
                )
              })}
            </View>
          ))}

          {disclosures.map((disclosure_group, i) => (
            <View key={`disclosures_${i}`}>
              {disclosure_group
                .filter(({ is_enabled }) => is_enabled)
                .map(({ markdown_text }) => (
                  <DesignSystem.Markdown
                    key={markdown_text}
                    style={{
                      body: { color: mesaColorNames['Greyscale/400 #A4AAAC'] },
                      paragraph: { marginTop: 0, marginBottom: 0 },
                    }}
                    children={markdown_text}
                  />
                ))}
            </View>
          ))}
        </ScrollView>
      </View>

      {showLearnMore && (
        <Components.LearnMoreModal ref={learnMoreModalRef} onClose={() => setIsOpen(false)}>
          <View style={{ paddingHorizontal: 6, marginBottom: 24 }}>
            {learnMoreAboutCardBenefits.map((instruction) => (
              <Components.Point text={instruction} key={instruction} />
            ))}
          </View>
        </Components.LearnMoreModal>
      )}
    </SafeAreaView>
  )
}
